import { useState, useCallback } from 'react'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'

// VTR item as returned by the API — flat list representing the tree structure via depth.
export interface VtrItem {
  depth: number
  isExpanded: boolean
  isFolder: boolean
  name: string
  info: string
}

interface RemoveVariablesResponse {
  removed: string[]
  foldersRemoved: number
  deletedFiles: string[]
  deleteErrors: string[]
}

interface RemoveFoldersResponse {
  itemsRemoved: number
  deletedFiles: string[]
  deleteErrors: string[]
}

export const useVartreeEditor = ({
  customer,
  job,
}: {
  customer: string
  job: string
}) => {
  const queryClient = useQueryClient()
  const [items, setItems] = useState<VtrItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set())

  const loadItems = useCallback(async () => {
    if (!customer || !job) return
    setIsLoading(true)
    setError(null)
    try {
      const res = await axios.get<{ items: VtrItem[] }>(
        `${API_BASE_URL}/job/${customer}/${job}/vtr/items`
      )
      setItems(res.data.items)
      setSelectedItems(new Set())
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message)
    } finally {
      setIsLoading(false)
    }
  }, [customer, job])

  const invalidateVartree = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['vartree'] })
  }, [queryClient])

  const saveItems = useCallback(async (newItems: VtrItem[]) => {
    setIsSaving(true)
    setError(null)
    try {
      await axios.put(`${API_BASE_URL}/job/${customer}/${job}/vtr`, { items: newItems })
      setItems(newItems)
      invalidateVartree()
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }, [customer, job, invalidateVartree])

  const removeVariables = useCallback(async (
    variables: string[],
    options: { deleteFiles?: boolean; cleanFolders?: boolean } = {}
  ) => {
    setIsSaving(true)
    setError(null)
    try {
      const res = await axios.post<RemoveVariablesResponse>(
        `${API_BASE_URL}/job/${customer}/${job}/vtr/remove-variables`,
        {
          variables,
          deleteFiles: options.deleteFiles || false,
          cleanFolders: options.cleanFolders ?? true,
        }
      )
      // Reload items after removal
      await loadItems()
      invalidateVartree()
      return res.data
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }, [customer, job, loadItems, invalidateVartree])

  const removeFolders = useCallback(async (
    folders: string[],
    options: { deleteFiles?: boolean } = {}
  ) => {
    setIsSaving(true)
    setError(null)
    try {
      const res = await axios.post<RemoveFoldersResponse>(
        `${API_BASE_URL}/job/${customer}/${job}/vtr/remove-folders`,
        {
          folders,
          deleteFiles: options.deleteFiles || false,
        }
      )
      await loadItems()
      invalidateVartree()
      return res.data
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }, [customer, job, loadItems, invalidateVartree])

  const createFolder = useCallback(async (path: string) => {
    setIsSaving(true)
    setError(null)
    try {
      await axios.post(`${API_BASE_URL}/job/${customer}/${job}/vtr/folders`, { path })
      await loadItems()
      invalidateVartree()
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }, [customer, job, loadItems, invalidateVartree])

  // Toggle selection of an item by index
  const toggleSelection = useCallback((index: number) => {
    setSelectedItems(prev => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }, [])

  // Select all items (optionally filtered to variables or folders only)
  const selectAll = useCallback((filter?: 'variables' | 'folders') => {
    const indices = new Set<number>()
    items.forEach((item, i) => {
      if (!filter) {
        indices.add(i)
      } else if (filter === 'variables' && !item.isFolder) {
        indices.add(i)
      } else if (filter === 'folders' && item.isFolder) {
        indices.add(i)
      }
    })
    setSelectedItems(indices)
  }, [items])

  const clearSelection = useCallback(() => {
    setSelectedItems(new Set())
  }, [])

  // Get selected variable names
  const getSelectedVariableNames = useCallback(() => {
    return Array.from(selectedItems)
      .filter(i => items[i] && !items[i].isFolder)
      .map(i => items[i].name)
  }, [selectedItems, items])

  // Get selected folder paths
  const getSelectedFolderPaths = useCallback(() => {
    return Array.from(selectedItems)
      .filter(i => items[i] && items[i].isFolder)
      .map(i => getFolderPath(items, i))
  }, [selectedItems, items])

  // Move an item up (swap with previous sibling at same depth)
  const moveItemUp = useCallback((index: number) => {
    if (index <= 0) return
    const item = items[index]

    // Find previous sibling at same depth
    let prevSiblingIdx = -1
    for (let i = index - 1; i >= 0; i--) {
      if (items[i].depth < item.depth) break // hit parent, stop
      if (items[i].depth === item.depth) {
        prevSiblingIdx = i
        break
      }
    }
    if (prevSiblingIdx < 0) return

    const newItems = [...items]

    // Collect the item and its children
    const itemBlock = collectBlock(newItems, index)
    const prevBlock = collectBlock(newItems, prevSiblingIdx)

    // Swap blocks: remove item block, insert before prev block
    const withoutItem = [
      ...newItems.slice(0, index),
      ...newItems.slice(index + itemBlock.length),
    ]
    // prevSiblingIdx is still valid since we removed after it
    const finalItems = [
      ...withoutItem.slice(0, prevSiblingIdx),
      ...itemBlock,
      ...withoutItem.slice(prevSiblingIdx),
    ]

    setItems(finalItems)
  }, [items])

  // Move an item down (swap with next sibling at same depth)
  const moveItemDown = useCallback((index: number) => {
    const item = items[index]
    const block = collectBlock(items, index)
    const afterBlockIdx = index + block.length

    if (afterBlockIdx >= items.length) return

    // Next sibling should be at the same depth
    if (items[afterBlockIdx].depth !== item.depth) return

    const nextBlock = collectBlock(items, afterBlockIdx)

    const newItems = [
      ...items.slice(0, index),
      ...nextBlock,
      ...block,
      ...items.slice(afterBlockIdx + nextBlock.length),
    ]

    setItems(newItems)
  }, [items])

  return {
    items,
    isLoading,
    isSaving,
    error,
    selectedItems,
    loadItems,
    saveItems,
    removeVariables,
    removeFolders,
    createFolder,
    toggleSelection,
    selectAll,
    clearSelection,
    getSelectedVariableNames,
    getSelectedFolderPaths,
    moveItemUp,
    moveItemDown,
    setItems,
  }
}

// Get the full folder path for a folder item at the given index
function getFolderPath(items: VtrItem[], index: number): string {
  const item = items[index]
  if (!item.isFolder) return item.name

  const parts: string[] = [item.name]
  const targetDepth = item.depth

  // Walk backwards to find parent folders
  for (let d = targetDepth - 1; d >= 0; d--) {
    for (let i = index - 1; i >= 0; i--) {
      if (items[i].isFolder && items[i].depth === d) {
        parts.unshift(items[i].name)
        break
      }
      if (items[i].depth < d) break
    }
  }

  return parts.join('/')
}

// Collect a block of items: the item at index plus all deeper children that follow
function collectBlock(items: VtrItem[], index: number): VtrItem[] {
  const block = [items[index]]
  const baseDepth = items[index].depth
  for (let i = index + 1; i < items.length; i++) {
    if (items[i].depth <= baseDepth) break
    block.push(items[i])
  }
  return block
}

export default useVartreeEditor
